import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin as PMPlugin, PluginKey as PMPluginKey, TextSelection } from 'prosemirror-state';
import { findParentNodeClosestToPos } from 'prosemirror-utils';
import { // @ts-ignore
__serializeForClipboard, Decoration, DecorationSet } from 'prosemirror-view';
import cn from 'classnames';
import editorStyles from '../../components/Editor/styles/base.scss';
export const safePos = (state, pos)=>{
    if (pos < 0) return 0;
    return Math.min(state.doc.content.size, pos);
};
export const getDOMByPos = (view, root, $pos)=>{
    const { node } = view.domAtPos($pos.pos);
    let el = node;
    let parent = el.parentElement;
    while(parent && parent !== root && $pos.pos === view.posAtDOM(parent, 0)){
        el = parent;
        parent = parent.parentElement;
    }
    return el;
};
export const selectAncestorNodeByDom = (dom, view)=>{
    const root = view.dom.parentElement;
    if (!root) return null;
    let pos = view.posAtDOM(dom, 0);
    /**
   * img Node corrections
   */ if (dom.tagName === 'IMG') {
        pos -= 1;
    }
    /**
   * Custom render node fixes
   */ if (dom && dom.classList.contains('react-renderer')) {
        const el = view.nodeDOM(pos);
        if (el === dom) {
            const $pos = view.state.doc.resolve(pos);
            let node = $pos.node();
            try {
                var _dom_className_match__split, _dom_className_match_, _dom_className_match, _dom_className;
                var _dom_className_match__split_shift;
                const nodeName = (_dom_className_match__split_shift = dom === null || dom === void 0 ? void 0 : (_dom_className = dom.className) === null || _dom_className === void 0 ? void 0 : (_dom_className_match = _dom_className.match(/node-(.+)(\S)?/)) === null || _dom_className_match === void 0 ? void 0 : (_dom_className_match_ = _dom_className_match[1]) === null || _dom_className_match_ === void 0 ? void 0 : (_dom_className_match__split = _dom_className_match_.split(' ')) === null || _dom_className_match__split === void 0 ? void 0 : _dom_className_match__split.shift()) !== null && _dom_className_match__split_shift !== void 0 ? _dom_className_match__split_shift : '';
                if (nodeName) {
                    if (node.type.name !== nodeName) {
                        var _$pos_nodeAfter_type, _$pos_nodeAfter;
                        if (($pos === null || $pos === void 0 ? void 0 : (_$pos_nodeAfter = $pos.nodeAfter) === null || _$pos_nodeAfter === void 0 ? void 0 : (_$pos_nodeAfter_type = _$pos_nodeAfter.type) === null || _$pos_nodeAfter_type === void 0 ? void 0 : _$pos_nodeAfter_type.name) === nodeName) {
                            node = $pos.nodeAfter;
                        }
                    }
                }
            } catch (e) {
            //
            }
            return {
                node,
                $pos,
                el,
                offset: 0
            };
        }
    }
    if (pos <= 0) return null;
    let $pos = view.state.doc.resolve(pos);
    let node = $pos.node();
    if (node.type.name === 'doc') {
        const nodeAtPos = view.state.doc.nodeAt(pos);
        if (nodeAtPos && nodeAtPos.type.name !== 'doc' && nodeAtPos.type.name !== 'text') {
            node = nodeAtPos;
            $pos = view.state.doc.resolve(pos);
            const el = view.nodeDOM(pos);
            return {
                node,
                $pos,
                el,
                offset: 0
            };
        }
    }
    if (node.type.name.includes('table')) {
        while(node.type.name !== 'table'){
            $pos = view.state.doc.resolve($pos.before());
            node = $pos.node();
        }
    }
    $pos = view.state.doc.resolve($pos.pos - $pos.parentOffset);
    const el = getDOMByPos(view, root, $pos);
    return {
        node,
        $pos,
        el,
        offset: 1
    };
};
const DragablePluginKey = new PMPluginKey('dragable');
export const Dragable = Extension.create({
    name: 'dragable',
    addProseMirrorPlugins () {
        let editorView;
        let dragHandleDOM;
        let activeNode;
        let activeSelection;
        let dragging = false;
        let mouseleaveTimer = null;
        const calculateBlockTextOffsetWhenAlignedLeft = (dom, root)=>{
            var _dom_parentElement_parentElement, _dom_parentElement, _dom_parentElement1, _dom_parentElement2, _dom_parentElement3;
            if ((dom === null || dom === void 0 ? void 0 : (_dom_parentElement = dom.parentElement) === null || _dom_parentElement === void 0 ? void 0 : (_dom_parentElement_parentElement = _dom_parentElement.parentElement) === null || _dom_parentElement_parentElement === void 0 ? void 0 : _dom_parentElement_parentElement.nodeName) === 'BLOCKQUOTE' || (dom === null || dom === void 0 ? void 0 : (_dom_parentElement1 = dom.parentElement) === null || _dom_parentElement1 === void 0 ? void 0 : _dom_parentElement1.nodeName) === 'BLOCKQUOTE') {
                return 0;
            }
            // TODO when P has text alignment center/right maybe use the P rect so the
            // handle is on the left not with the text.
            let paragraph;
            if ((dom === null || dom === void 0 ? void 0 : (_dom_parentElement2 = dom.parentElement) === null || _dom_parentElement2 === void 0 ? void 0 : _dom_parentElement2.nodeName) !== 'P') {
                var _dom_parentElement4;
                paragraph = dom === null || dom === void 0 ? void 0 : (_dom_parentElement4 = dom.parentElement) === null || _dom_parentElement4 === void 0 ? void 0 : _dom_parentElement4.closest('p');
            }
            if ((dom === null || dom === void 0 ? void 0 : (_dom_parentElement3 = dom.parentElement) === null || _dom_parentElement3 === void 0 ? void 0 : _dom_parentElement3.nodeName) === 'P') {
                paragraph = dom.parentElement;
            }
            if (dom.nodeName === 'P') {
                paragraph = dom;
            }
            const textNode = paragraph === null || paragraph === void 0 ? void 0 : paragraph.childNodes[0];
            if (!textNode || textNode.textContent === '' || !root) {
                return 0;
            }
            // This is the editors container
            const rootRect = root.getBoundingClientRect();
            // Create range so we can measure difference between the text position
            // and the root box
            const range = document.createRange();
            range.setStart(textNode, 0);
            range.setEnd(textNode, 1);
            const rects = range.getClientRects();
            if (rects.length === 0) {
                return 0;
            }
            // Just get the first line
            const rangeRect = rects[0];
            // Keeping this as its very useful for debugging
            /* const debugRect2 = document.getElementById('debug-rect2');
      const debug2 = debugRect2 ?? document.createElement('div');
      if (!debugRect2) {
        document.body.appendChild(debug2);
      }

      const styles2: React.CSSProperties = {
        position: 'absolute',
        width: `${rangeRect.width}px`,
        height: `${rangeRect.height}px`,
        left: `${rangeRect.left + window.scrollX}px`,
        top: `${rangeRect.top + window.scrollY}px`,
      };

      debug2.setAttribute('id', 'debug-rect2');
      debug2.style.position = 'absolute';
      debug2.style.backgroundColor = '#ff0000aa';
      debug2.style.width = `${rangeRect.width}px`;
      debug2.style.height = `${rangeRect.height}px`;
      debug2.style.left = styles2.left as any;
      debug2.style.top = styles2.top as any;
      debug2.style.zIndex = '999999';
      debug2.style.minWidth = '10px';
      debug2.style.pointerEvents = 'none';
      debug2.style.opacity = '0.8';*/ // There is no offset so this block is not flowing around another block
            if (rootRect.x === rangeRect.x) {
                return 0;
            }
            // Set the left offset for the handler accounting for the parent
            // which has relative position applied
            return rangeRect.left - rootRect.left - 24;
        };
        const createDragHandleDOM = ()=>{
            const dom = document.createElement('div');
            dom.className = cn('dragable', editorStyles['draggable']);
            dom.draggable = true;
            dom.setAttribute('data-drag-handle', 'true');
            return dom;
        };
        const showDragHandleDOM = ()=>{
            var _dragHandleDOM_classList, _dragHandleDOM_classList1;
            dragHandleDOM === null || dragHandleDOM === void 0 ? void 0 : (_dragHandleDOM_classList = dragHandleDOM.classList) === null || _dragHandleDOM_classList === void 0 ? void 0 : _dragHandleDOM_classList.add('show', editorStyles['dh-show']);
            dragHandleDOM === null || dragHandleDOM === void 0 ? void 0 : (_dragHandleDOM_classList1 = dragHandleDOM.classList) === null || _dragHandleDOM_classList1 === void 0 ? void 0 : _dragHandleDOM_classList1.remove('hide', editorStyles['dh-hide']);
        };
        const hideDragHandleDOM = ()=>{
            var _dragHandleDOM_classList, _dragHandleDOM_classList1, _dragHandleDOM_classList2;
            dragHandleDOM === null || dragHandleDOM === void 0 ? void 0 : (_dragHandleDOM_classList = dragHandleDOM.classList) === null || _dragHandleDOM_classList === void 0 ? void 0 : _dragHandleDOM_classList.remove('show', editorStyles['dh-show']);
            dragHandleDOM === null || dragHandleDOM === void 0 ? void 0 : (_dragHandleDOM_classList1 = dragHandleDOM.classList) === null || _dragHandleDOM_classList1 === void 0 ? void 0 : _dragHandleDOM_classList1.remove('active', editorStyles['dh-active']);
            dragHandleDOM === null || dragHandleDOM === void 0 ? void 0 : (_dragHandleDOM_classList2 = dragHandleDOM.classList) === null || _dragHandleDOM_classList2 === void 0 ? void 0 : _dragHandleDOM_classList2.add('hide', editorStyles['dh-hide']);
        };
        const renderDragHandleDOM = (view, referenceRectDOM, offsetLeftAdjustment)=>{
            const root = view.dom.parentElement;
            if (!root) {
                return;
            }
            const targetNodeRect = referenceRectDOM.getBoundingClientRect();
            const rootRect = root.getBoundingClientRect();
            const handleRect = dragHandleDOM.getBoundingClientRect();
            const handleWidth = Math.max(24, handleRect.width); // sometimes it was zero...
            const computedStyles = window.getComputedStyle(referenceRectDOM);
            // Start handle left offset when when tab stuff
            const start = parseInt(computedStyles.marginInlineStart);
            // Deals with lists needing to avoid their bullets/numbers
            let listOffset = 0;
            if (referenceRectDOM.tagName === 'LI') {
                listOffset = referenceRectDOM.getAttribute('data-checked') ? -6 : -32;
            }
            const left = targetNodeRect.left - rootRect.left - handleWidth + listOffset;
            const top = targetNodeRect.top - rootRect.top;
            let offsetLeft = 0;
            // Keep the handle on the left when indenting blocks
            if (start && !Number.isNaN(start)) {
                offsetLeft = offsetLeft - start;
            }
            if (offsetLeftAdjustment) {
                dragHandleDOM.style.left = `${offsetLeftAdjustment + listOffset}px`;
            } else {
                dragHandleDOM.style.left = `${left + offsetLeft}px`;
            }
            dragHandleDOM.style.top = `${top}px`;
            showDragHandleDOM();
        };
        const handleMouseEnter = ()=>{
            if (!activeNode) return null;
            clearTimeout(mouseleaveTimer);
            showDragHandleDOM();
        };
        const handleMouseLeave = ()=>{
            if (!activeNode) return null;
            hideDragHandleDOM();
        };
        const handleMouseDown = ()=>{
            if (!activeNode) return null;
            if (NodeSelection.isSelectable(activeNode.node)) {
                const nodeSelection = NodeSelection.create(editorView.state.doc, activeNode.$pos.pos - activeNode.offset);
                editorView.dispatch(editorView.state.tr.setSelection(nodeSelection));
                editorView.focus();
                activeSelection = nodeSelection;
                return nodeSelection;
            }
            return null;
        };
        const handleMouseUp = ()=>{
            if (!dragging) return;
            dragging = false;
            activeSelection = null;
            activeNode = null;
        };
        const handleDragStart = (event)=>{
            dragging = true;
            if (event.dataTransfer && activeSelection) {
                const slice = activeSelection.content();
                event.dataTransfer.effectAllowed = 'copyMove';
                const { dom, text } = __serializeForClipboard(editorView, slice);
                event.dataTransfer.clearData();
                event.dataTransfer.setData('text/html', dom.innerHTML);
                event.dataTransfer.setData('text/plain', text);
                if (activeNode === null || activeNode === void 0 ? void 0 : activeNode.el.classList.contains('node-da-deviation')) {
                    // Fixes the preview containing the surrounding lines due to resize handles
                    const figure = activeNode === null || activeNode === void 0 ? void 0 : activeNode.el.querySelector('figure');
                    if (figure) {
                        event.dataTransfer.setDragImage(figure.firstChild, 0, 0);
                    }
                } else if (activeNode === null || activeNode === void 0 ? void 0 : activeNode.el.classList.contains('node-da-deviation-thumb')) {
                    // Fixes the preview containing the surrounding lines due to resize handles
                    const figure = activeNode === null || activeNode === void 0 ? void 0 : activeNode.el.querySelector('img');
                    if (figure) {
                        event.dataTransfer.setDragImage(figure.firstChild, 0, 0);
                    }
                } else {
                    event.dataTransfer.setDragImage(activeNode === null || activeNode === void 0 ? void 0 : activeNode.el, 0, 0);
                }
                editorView.dragging = {
                    slice,
                    move: true
                };
            }
        };
        return [
            new PMPlugin({
                key: DragablePluginKey,
                view: (view)=>{
                    if (view.editable && view.dom.parentNode) {
                        var _view_dom_parentNode;
                        dragHandleDOM = createDragHandleDOM();
                        dragHandleDOM.addEventListener('mouseenter', handleMouseEnter);
                        dragHandleDOM.addEventListener('mouseleave', handleMouseLeave);
                        dragHandleDOM.addEventListener('mousedown', handleMouseDown);
                        dragHandleDOM.addEventListener('mouseup', handleMouseUp);
                        dragHandleDOM.addEventListener('dragstart', handleDragStart);
                        (_view_dom_parentNode = view.dom.parentNode) === null || _view_dom_parentNode === void 0 ? void 0 : _view_dom_parentNode.appendChild(dragHandleDOM);
                    }
                    return {
                        update (v) {
                            editorView = v;
                        },
                        destroy: ()=>{
                            if (!dragHandleDOM) return;
                            clearTimeout(mouseleaveTimer);
                            dragHandleDOM.removeEventListener('mouseenter', handleMouseEnter);
                            dragHandleDOM.removeEventListener('mouseleave', handleMouseLeave);
                            dragHandleDOM.removeEventListener('mousedown', handleMouseDown);
                            dragHandleDOM.removeEventListener('mouseup', handleMouseUp);
                            dragHandleDOM.removeEventListener('dragstart', handleDragStart);
                            dragHandleDOM.remove();
                        }
                    };
                },
                props: {
                    handleDOMEvents: {
                        drop: (view, event)=>{
                            if (!view.editable || !dragHandleDOM) return false;
                            if (!activeSelection) return false;
                            const eventPos = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY
                            });
                            setTimeout(()=>{
                                if (activeSelection) {
                                    [
                                        'ProseMirror-selectednode',
                                        'ProseMirror-selectedblocknode-dragable',
                                        'ProseMirror-selectedblocknode-normal'
                                    ].forEach((cls)=>{
                                        view.dom.querySelectorAll(`.${cls}`).forEach((dom)=>dom.classList.remove(cls));
                                    });
                                    var _eventPos_pos;
                                    const noneSelection = new TextSelection(view.state.doc.resolve(safePos(view.state, (_eventPos_pos = eventPos === null || eventPos === void 0 ? void 0 : eventPos.pos) !== null && _eventPos_pos !== void 0 ? _eventPos_pos : 0)));
                                    view.dispatch(view.state.tr.setSelection(noneSelection));
                                    activeSelection = null;
                                    activeNode = null;
                                }
                            }, 100);
                            this.editor.commands.focus();
                            if (!eventPos) {
                                return true;
                            }
                            return false;
                        },
                        mousemove: (view, event)=>{
                            var _view_domAtPos;
                            if (!view.editable || !dragHandleDOM) {
                                return false;
                            }
                            const coords = {
                                left: event.clientX,
                                top: event.clientY
                            };
                            const pos = view.posAtCoords(coords);
                            if (!pos || pos.pos < 0) {
                                return false;
                            }
                            let dom = view.nodeDOM(pos.pos) || ((_view_domAtPos = view.domAtPos(pos.pos)) === null || _view_domAtPos === void 0 ? void 0 : _view_domAtPos.node) || event.target;
                            const offsetLeftAdjustment = calculateBlockTextOffsetWhenAlignedLeft(dom, view.dom.parentElement);
                            const maybeTaskItemOrListItem = findParentNodeClosestToPos(view.state.doc.resolve(pos.pos), (node)=>[
                                    'taskItem',
                                    'listItem'
                                ].some((name)=>name === node.type.name));
                            const maybeBlockquote = findParentNodeClosestToPos(view.state.doc.resolve(pos.pos), (node)=>[
                                    'blockquote'
                                ].some((name)=>name === node.type.name));
                            if (!dom) {
                                if (dragging) {
                                    return false;
                                }
                                hideDragHandleDOM();
                                return false;
                            }
                            // 3 = textnode
                            while(dom && dom.nodeType === 3){
                                dom = dom.parentElement;
                            }
                            // Select blockquote
                            if (maybeBlockquote) {
                                while(dom && dom.tagName !== 'BLOCKQUOTE'){
                                    dom = dom.parentElement;
                                }
                            }
                            // Select the list item
                            if (maybeTaskItemOrListItem) {
                                while(dom && dom.tagName !== 'LI'){
                                    dom = dom.parentElement;
                                }
                            }
                            if (!maybeBlockquote && dom.tagName === 'LI') {
                                var _dom_parentElement;
                                if ((dom === null || dom === void 0 ? void 0 : (_dom_parentElement = dom.parentElement) === null || _dom_parentElement === void 0 ? void 0 : _dom_parentElement.childElementCount) === 1) {
                                    return false;
                                }
                            }
                            // The entire list is not allowed to be selected
                            if (!maybeBlockquote && (dom.tagName === 'UL' || dom.tagName === 'OL')) {
                                return false;
                            }
                            try {
                                var _maybeReactRenderer_classList, _maybeReactRenderer_classList1, _maybeReactRenderer_classList2;
                                let maybeReactRenderer = dom;
                                // Inline images
                                if ((_maybeReactRenderer_classList = maybeReactRenderer.classList) === null || _maybeReactRenderer_classList === void 0 ? void 0 : _maybeReactRenderer_classList.contains('node-da-deviation-thumb')) {
                                    return false;
                                }
                                while(maybeReactRenderer && !((_maybeReactRenderer_classList1 = maybeReactRenderer.classList) === null || _maybeReactRenderer_classList1 === void 0 ? void 0 : _maybeReactRenderer_classList1.contains('react-renderer'))){
                                    maybeReactRenderer = maybeReactRenderer.parentElement;
                                }
                                if (maybeReactRenderer && !(maybeReactRenderer === null || maybeReactRenderer === void 0 ? void 0 : (_maybeReactRenderer_classList2 = maybeReactRenderer.classList) === null || _maybeReactRenderer_classList2 === void 0 ? void 0 : _maybeReactRenderer_classList2.contains('node-columns'))) {
                                    dom = maybeReactRenderer;
                                }
                            } catch (e) {
                            //
                            }
                            if (!(dom instanceof Element)) {
                                if (dragging) {
                                    return false;
                                }
                                hideDragHandleDOM();
                                return false;
                            }
                            const result = selectAncestorNodeByDom(dom, view);
                            if (!result || result.node.type.name === 'doc' || result.node.type.name === 'tableOfContents') {
                                if (dragging) return false;
                                hideDragHandleDOM();
                                return false;
                            }
                            activeNode = result;
                            renderDragHandleDOM(view, result.el, offsetLeftAdjustment);
                            return false;
                        },
                        keydown: ()=>{
                            if (!editorView.editable || !dragHandleDOM) return false;
                            hideDragHandleDOM();
                            return false;
                        },
                        mouseleave: ()=>{
                            clearTimeout(mouseleaveTimer);
                            mouseleaveTimer = setTimeout(()=>{
                                hideDragHandleDOM();
                            }, 400);
                            return false;
                        }
                    }
                }
            }),
            new PMPlugin({
                key: new PMPluginKey('AncestorDragablePluginFocusKey'),
                props: {
                    decorations (state) {
                        const usingActiveSelection = !!activeSelection;
                        const selection = state.selection;
                        if (selection instanceof NodeSelection) {
                            const { from, to } = selection;
                            return DecorationSet.create(state.doc, [
                                Decoration.node(safePos(state, from), safePos(state, to), {
                                    class: usingActiveSelection ? 'ProseMirror-selectedblocknode-dragable' : 'ProseMirror-selectedblocknode-normal'
                                })
                            ]);
                        }
                        return DecorationSet.empty;
                    }
                }
            })
        ];
    }
});
export default Dragable;

//# sourceMappingURL=draggable.js.map